<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMatchMedia } from '@/utils'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import StreamsWidgetLiveStreamTab from './StreamsWidgetLiveStreamTab.vue'
import StreamsWidgetVodTab from './StreamsWidgetVodTab.vue'
import StreamsWidgetPlaylistTab from './StreamsWidgetPlaylistTab.vue'
import StreamsWidgetBreaks from './StreamsWidgetBreaks.vue'
import FormButton from '../Atoms/FormButton.vue'
import IconBase from '../icon/IconBase.vue'
import IconPlus from '../icon/IconPlus.vue'
import IconCross from '../icon/IconCross.vue'
import Badge from '../Badge.vue'
import TabGroup from '../Atoms/TabGroup.vue'
import TabItem from '../Atoms/TabItem.vue'

const route = useRoute()
const router = useRouter()
const { notify } = useNotification()
const store = useStore()

// Props
const props = defineProps({
  placement: {
    type: String,
    default: '#streams-widget-wrapper'
  },
  source: {
    type: String,
    default: 'playout'
  },
  hideNotification: {
    type: Boolean,
    default: false
  },
  closeAfterSelect: {
    type: Boolean,
    default: false
  },
  onlyLive: {
    type: Boolean,
    default: false
  },
  multiview: {
    type: Boolean,
    default: false
  },
  hideLive: {
    type: Boolean,
    default: false,
  },
  disabledStreamIds: {
    type: Array,
    default: () => []
  },
  variant: {
    type: String,
    default: 'default'
  },
  tabSize: {
    type: String || null,
    default: null
  },
  itemsPerPage: {
    type: Number,
    default: 5,
  },
  itemsPerPageOptions: {
    type: Array,
    default: () => [5, 10, 20],
  },
  showItemsPerPageSelector: {
    type: Boolean,
    default: false,
  },
  showPlusButton: {
    type: Boolean,
    default: true,
  },
  headerTitle: {
    type: String,
    default: 'Select source',
  },
  selectedId: {
    type: String,
    default: null,
  },
})

// Emits
const emit = defineEmits({
  'add-stream': () => true,
  'close': () => true,
})

onMounted(async () => {
  if (props.hideLive) {
    router.push({ query: { sourceTab: 'vod'}})
  }
  if (props.onlyLive) {
    router.push({ query: { sourceTab: 'livestream'}})
  }
})


// CONST
const isDesktop = useMatchMedia('(min-width: 768px)')

// Refs
const isVisible = ref(false)

// Computed

const computedWrapperClass = computed(() => {
  const basicClass = '!px-4 md:!px-0 md:!visible transition-transform duration-500 md:translate-y-0 fixed md:static bottom-0 left-0 z-[1050] bg-surface-2 md:bg-transparent w-full rounded-t-2xl !shadow-lg md:!shadow-none'
  const closeClass = 'ease-in translate-y-full'
  const openClass = 'ease-out translate-y-0'
  return `${basicClass} ${isVisible.value ? openClass : closeClass}`
})

const computedOverlayClass = computed(() => {
  const basicClass = 'bg-black/20 absolute z-10 h-full w-full top-0 left-0'
  const closeClass = '!invisible'
  const openClass = '!visible open'
  return `${basicClass} ${isVisible.value ? openClass : closeClass}`
})

const shouldShowPlaylistTab = computed(() => props.source !== 'playout' && props.source !== 'dynamic' && props.source !== 'mediaflow' && props.source !== 'playlist' && props.source !== 'showcase' && !props.onlyLive)

const wrapperClasses = computed(() => {
  return props.variant === 'default' ? 'mb-6 w-full' : 'mt-6 lg:mt-0 mb-6 !w-full bg-surface-3 !rounded-lg'
})
// Methods
const changeRouteQuery = (routeQuery) => {
  if (route.query?.sourceTab != routeQuery) router.push({ query: { sourceTab: routeQuery } })
}

const tabItemClasses = computed(() => {
  return props.tabSize ? 'basis-' + props.tabSize : ''
})

const addStream = (stream, type) => {
  console.log('addStream', stream, type)
  if (stream && !isDesktop.value && !props.hideNotification) {
    notify({ group: 'success', text: `${stream.type === 'live' ? stream.name : stream.fileName} added` })
  } else if (!isDesktop.value && !props.hideNotification) {
    notify({ group: 'success', text: `Break added` })
  }
  emit('add-stream', stream, type)
  if (props.closeAfterSelect) toggleView()
}

// Which tab opens when the route has no `sourceTab` yet. Sites picks the first
// tab that actually has something to choose (VOD files → livestreams →
// playlists) instead of landing on an empty VOD list; other sources keep VOD.
const defaultTab = computed(() => {
  if (props.onlyLive) return 'livestream'
  if (props.source !== 'sites') return 'vod'
  if (store.getters['Streams/hasVodFiles']) return 'vod'
  if (!props.hideLive && (store.getters['Streams/allInOneStreams'] || []).length) return 'livestream'
  if (shouldShowPlaylistTab.value && (store.getters['Playlist/playlist'] || []).length) return 'playlist'
  return 'vod'
})
const activeTab = computed(() => route.query?.sourceTab || defaultTab.value)
const showTab = (tabName) => activeTab.value === tabName

const openView = () => {
  isVisible.value = true
}

const closeView = ({ emitEvent = true } = {}) => {
  if (!isVisible.value) return
  isVisible.value = false
  if (emitEvent) emit('close')
}

const toggleView = () => {
  if (isVisible.value) {
    closeView()
  } else {
    openView()
  }
}

// body:has(.modal-overlay.open) { overflow: hidden } (main.css) removes the
// viewport scrollbar and shifts layout. Compensate with padding-right — same
// approach as Sidebar/ModalBox. Measure before the .open class hits the DOM
// (watch flush: pre) so the width is still accurate.
let lockedPaddingRight = null

const compensateScrollbar = () => {
  if (lockedPaddingRight !== null || isDesktop.value) return
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
  lockedPaddingRight = document.body.style.paddingRight
  if (scrollbarWidth > 0) {
    const current = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0
    document.body.style.paddingRight = `${current + scrollbarWidth}px`
  }
}

const clearScrollbarCompensation = () => {
  if (lockedPaddingRight === null) return
  document.body.style.paddingRight = lockedPaddingRight
  lockedPaddingRight = null
}

watch(isVisible, (visible) => {
  visible ? compensateScrollbar() : clearScrollbarCompensation()
})

onBeforeUnmount(clearScrollbarCompensation)

defineExpose({ toggleView, openView, closeView })

</script>

<template>
  <teleport :disabled="isDesktop" :to='props.placement'>
    <div :class="computedWrapperClass">
      <header class="flex md:hidden items-center justify-between bg-surface-3 rounded-t-2xl -mx-4 p-3">
        <h3>{{ headerTitle }}</h3>
        <div class="md:hidden rounded-full bg-surface-4">
          <FormButton type="link" @click="toggleView" isIcon>
            <icon-base>
              <icon-cross />
            </icon-base>
          </FormButton>
        </div>
      </header>
      <!-- Default variant: underline tabs -->
      <div v-if="!multiview && props.variant === 'default'"
        class="flex items-center gap-5 mb-4 border-b border-surface-3 overflow-x-auto max-w-full no-scrollbar">
        <FormButton @click="changeRouteQuery('livestream')" :data-active="activeTab === 'livestream' ? 'true' : 'false'" data-testid="source-tab-livestream"
          :custom-classes="'relative -mb-px pb-2.5 text-sm font-medium text-nowrap border-b-2 transition-colors ' + (activeTab === 'livestream'
            ? 'text-white border-main'
            : 'text-surface-7 border-transparent hover:text-white')">
          Livestreams
        </FormButton>
        <FormButton v-if="!props.onlyLive" @click="changeRouteQuery('vod')" :data-active="activeTab === 'vod' ? 'true' : 'false'" data-testid="source-tab-vod"
          :custom-classes="'relative -mb-px pb-2.5 text-sm font-medium text-nowrap border-b-2 transition-colors ' + (activeTab === 'vod'
            ? 'text-white border-main'
            : 'text-surface-7 border-transparent hover:text-white')">
          VOD Files
        </FormButton>
        <FormButton v-if="shouldShowPlaylistTab" @click="changeRouteQuery('playlist')" :data-active="activeTab === 'playlist' ? 'true' : 'false'" data-testid="source-tab-playlist"
          :custom-classes="'relative -mb-px pb-2.5 text-sm font-medium text-nowrap border-b-2 transition-colors ' + (activeTab === 'playlist'
            ? 'text-white border-main'
            : 'text-surface-7 border-transparent hover:text-white')">
          Playlist
        </FormButton>
      </div>

      <!-- Other variants (buttons / segmented) keep existing layout -->
      <TabGroup v-else-if="!multiview" type="buttons" :class="wrapperClasses">
        <TabItem type="buttons-3" @click="changeRouteQuery('livestream')" centered
          :isActive="activeTab === 'livestream'" :class="tabItemClasses">
          Livestreams
        </TabItem>
        <TabItem type="buttons-3" v-if="!props.onlyLive" @click="changeRouteQuery('vod')" centered
          :isActive="activeTab === 'vod'" :class="tabItemClasses">
          VOD Files
        </TabItem>
        <TabItem type="buttons-3" v-if="shouldShowPlaylistTab" @click="changeRouteQuery('playlist')" centered
          :isActive="activeTab === 'playlist'" :class="tabItemClasses">
          Playlist
        </TabItem>
      </TabGroup>
      <section>
        <StreamsWidgetLiveStreamTab  v-if="!hideLive" :source="props.source" :multiview="props.multiview"
          @add-livestream="value => addStream(value, 'livestream')" v-show="showTab('livestream')"
          :disabled-stream-ids="props.disabledStreamIds" :variant="props.variant"
          :selected-id="props.selectedId"
          :items-per-page="props.itemsPerPage"
          :items-per-page-options="props.itemsPerPageOptions"
          :show-items-per-page-selector="props.showItemsPerPageSelector">
          <StreamsWidgetBreaks v-if="props.source === 'playout'" @add-break="addStream" class="mb-2" />
        </StreamsWidgetLiveStreamTab>
        <StreamsWidgetVodTab v-if="!props.onlyLive" :source="props.source" @add-vod="value => addStream(value, 'vod')"
          v-show="showTab('vod')" :variant="props.variant"
          :selected-id="props.selectedId"
          :items-per-page="props.itemsPerPage"
          :items-per-page-options="props.itemsPerPageOptions"
          :show-items-per-page-selector="props.showItemsPerPageSelector">
          <StreamsWidgetBreaks v-if="props.source === 'playout'" @add-break="addStream" class="mb-2" />
        </StreamsWidgetVodTab>
        <StreamsWidgetPlaylistTab v-if="shouldShowPlaylistTab" @add-playlist="value => addStream(value, 'playlist')"
          v-show="showTab('playlist')" :variant="props.variant" :selected-id="props.selectedId">
        </StreamsWidgetPlaylistTab>
      </section>
      <footer>
        <slot name="footer" />
      </footer>
    </div>
  </teleport>
  <teleport to='body'>
    <div class="modal-overlay" :class="computedOverlayClass"></div>
  </teleport>
  <FormButton v-if="props.showPlusButton" type="secondary" @click="toggleView" isIcon class="md:hidden shrink-0">
    <icon-base>
      <icon-plus />
    </icon-base>
  </FormButton>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
